/**
 * Pendo consumer package
 *
 * @packageDocumentation
 */
var _a, _b, _c, _d, _e, _f;
import { registerLibrary } from '@onviobr/app-information';
registerLibrary({
    name: '@tr/pendo',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    version: "6.2.0",
});
const pendoGlobalName = 'pendo';
// eslint-disable-next-line no-multi-assign
const pendoGlobal = (window[pendoGlobalName] = window[pendoGlobalName] || {});
const pendoScriptUrl = (pendoAPIKey) => `https://content-5695976203485184.analytics.thomsonreuters.com/agent/static/${pendoAPIKey}/pendo.js`;
// Checks if code is running in a local server or not
const isLocalEnvironment = /^localhost|127\.0\.0\.1/.test(globalThis.location.hostname);
const shouldRunPendo = () => globalThis.sessionStorage.getItem('override-pendo-should-run')
    ? Boolean(globalThis.sessionStorage.getItem('override-pendo-should-run'))
    : !isLocalEnvironment;
function genericPendoLazyFunction(method, ...args) {
    pendoGlobal._q[method === 'identify' ? 'unshift' : 'push']([method, ...args]);
}
/**
 * Bootstrap the `pendo` namespace for deferred execution of common methods
 * until the Agent is fully loaded from the CDN.
 */
pendoGlobal._q = (_a = pendoGlobal._q) !== null && _a !== void 0 ? _a : [];
pendoGlobal.initialize = (_b = pendoGlobal.initialize) !== null && _b !== void 0 ? _b : genericPendoLazyFunction.bind(null, 'initialize');
pendoGlobal.identify = (_c = pendoGlobal.identify) !== null && _c !== void 0 ? _c : genericPendoLazyFunction.bind(null, 'identify');
pendoGlobal.updateOptions = (_d = pendoGlobal.updateOptions) !== null && _d !== void 0 ? _d : genericPendoLazyFunction.bind(null, 'updateOptions');
pendoGlobal.pageLoad = (_e = pendoGlobal.pageLoad) !== null && _e !== void 0 ? _e : genericPendoLazyFunction.bind(null, 'pageLoad');
pendoGlobal.track = (_f = pendoGlobal.track) !== null && _f !== void 0 ? _f : genericPendoLazyFunction.bind(null, 'track');
async function injectScript(pendoAPIKey) {
    if (!shouldRunPendo()) {
        return Promise.resolve(false);
    }
    const pendoUrl = pendoScriptUrl(pendoAPIKey);
    return new Promise((resolve, reject) => {
        // If pendo is already loaded just return the global
        if (document.body.querySelector(`script[data-tr-pendo-injector]`)) {
            resolve(false);
            return;
        }
        const script = document.createElement('script');
        script.setAttribute('data-tr-pendo-injector', '');
        script.async = true;
        script.src = pendoUrl;
        script.onload = () => resolve(true);
        script.onerror = reject;
        let lastScript = document.getElementsByTagName('script')[0];
        if (lastScript === undefined) {
            lastScript = document.body.lastChild;
        }
        if (lastScript) {
            lastScript.parentNode.insertBefore(script, lastScript);
        }
        else {
            document.body.appendChild(script);
        }
    });
}
/**
 * Injects and initialize Pendo Agent into the current application
 * @public
 * @param apiKey - Pendo API key
 * @param options - Pendo Agent options
 * @returns Promise that resolves when the Agent is fully initialized
 */
export async function initialize(apiKey, options) {
    const result = await injectScript(apiKey);
    if (result) {
        pendoGlobal.initialize({
            apiKey,
            ...options,
        });
    }
    return result;
}
/**
 * Creates a track event in Pendo with the Pendo Agent
 * @public
 * @param trackType - The type of the track event
 * @param metadata - The metadata to be sent with the track event
 */
export function trackEvent(trackType, metadata) {
    pendoGlobal.track(trackType, metadata);
}
/**
 * Send an identify event and a meta event.
 * @public
 * @param options - If string has to be the visitor id, if object has to be the visitor data
 * @param accountId - The account id to be sent with the identify event (only supported when options is string)
 */
export function identify(options, accountId) {
    pendoGlobal.identify(options, accountId);
}
/**
 * Returns true when the Agent is fully loaded and has an API key.
 * @public
 */
export function isReady() {
    return pendoGlobal.isReady();
}
/**
 * Force a flush of cached event objects
 * @public
 * @returns Promise that resolved when all object are flushed or rejects if there is an error
 */
export async function flushNow() {
    return pendoGlobal.flushNow();
}
/**
 * After ${@link initialize} has been called, updates one or more visitor metadata fields.
 * @public
 * @param options - Pendo Agent options
 */
export function updateOptions(options) {
    pendoGlobal.updateOptions(options);
}
/**
 * Returns the string for the current Version of the Agent.
 * @public
 */
export function getVersion() {
    return pendoGlobal.getVersion();
}
/**
 * Returns the id for the visitor currently being applied to any events being sent to Pendo.
 * Will always be set either by host application or as a generated id.
 * @public
 */
export function getVisitorId() {
    return pendoGlobal.getVisitorId();
}
/**
 * Returns the id for the account currently being applied to any events being sent to Pendo.
 * @public
 */
export function getAccountId() {
    return pendoGlobal.getAccountId();
}
/**
 * Gets the current URL that is sent on all events.
 * @public
 */
export function getCurrentUrl() {
    return pendoGlobal.getCurrentUrl();
}
/**
 * Returns a Guide object for the given name, if loaded.
 * @public
 * @param name - The name of the guide to be retrieved
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findGuideByName(name) {
    return pendoGlobal.findGuideByName(name);
}
/**
 * Returns a Guide object for the given id, if loaded.
 * @public
 * @param id - The ID of the guide to be retrieved
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findGuideById(id) {
    return pendoGlobal.findGuideById(id);
}
/**
 * Activates the Guide for the given name, if loaded.
 * @public
 * @param name - The name of the guide to be activated
 */
export function showGuideByName(name) {
    pendoGlobal.showGuideByName(name);
}
/**
 * Activates the Guide for the given ID, if loaded.
 * @public
 * @param id - The ID of the guide to be activated
 */
export function showGuideById(id) {
    pendoGlobal.showGuideById(id);
}
/**
 * Causes the Guide Center to toggle visibility, if the Guide Center is eligible for the current URL.
 * @public
 */
export function toggleLauncher() {
    pendoGlobal.toggleLauncher();
}
/**
 * Removes the Guide Center completely until a full page reload.
 * @public
 */
export function removeLauncher() {
    pendoGlobal.removeLauncher();
}
/**
 * Add listener to when the Agent is loaded and initialized, i.e. isReady would return true.
 * @public
 * @param cb - Callback to be called when the Agent is loaded and initialized
 */
export function ready(cb) {
    pendoGlobal.events.ready(cb);
}
/**
 * Add listener to when Guides have loaded successfully, including any associated content assets.
 * @public
 * @param cb - Callback to be called when the Guides have loaded successfully
 */
export function guidesLoaded(cb) {
    pendoGlobal.events.guidesLoaded(cb);
}
/**
 * Add listener to when Guides fail to load because of a network error or timeout,
 * when guideTimeout or `guides.timeout` is set to a numeric value in milliseconds.
 * @public
 * @param cb - Callback to be called when the Guides fail to load
 */
export function guidesFailed(cb) {
    pendoGlobal.events.guidesFailed(cb);
}
